import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { trackEvent, Events } from "@/lib/analytics";

import { API } from "@/lib/apiBase";

const AuthCallback = () => {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing");
  // useRef guard — synchronous, not subject to React 18 StrictMode double-invocation races.
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const fullUrl = window.location.href;
    const hash = window.location.hash || "";

    let sessionId = null;
    const hashMatch = hash.match(/session_id=([^&]+)/);
    if (hashMatch) sessionId = hashMatch[1];
    if (!sessionId) {
      const urlMatch = fullUrl.match(/session_id=([^&]+)/);
      if (urlMatch) sessionId = urlMatch[1];
    }

    if (!sessionId) {
      setStatus("error");
      toast.error("Login failed. Please try again.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
      return;
    }

    const processSession = async () => {
      try {
        await googleLogin(sessionId);
        trackEvent(Events.LOGIN_COMPLETED, { method: "google" });
        window.history.replaceState(null, "", "/");
        navigate("/", { replace: true });
      } catch (err) {
        console.warn("Google auth first attempt failed, retrying...", err?.response?.status);
        // Backend /auth/google/session is now idempotent (caches session_id for 5 min),
        // so a retry is guaranteed to succeed if the first attempt was a transient failure.
        try {
          await new Promise((r) => setTimeout(r, 500));
          const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
          if (meRes?.data?.id) {
            window.history.replaceState(null, "", "/");
            navigate("/", { replace: true });
            return;
          }
        } catch { /* fall through */ }
        try {
          await googleLogin(sessionId);
          window.history.replaceState(null, "", "/");
          navigate("/", { replace: true });
        } catch (err2) {
          console.error("Google auth retry failed:", err2);
          setStatus("error");
          toast.error("Login failed. Please try again.");
          setTimeout(() => navigate("/login", { replace: true }), 2000);
        }
      }
    };

    // Tiny delay so cookies set during the redirect are committed before processing
    setTimeout(processSession, 100);
  }, [navigate, googleLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#0A0A0C" }}>
      <div className="flex flex-col items-center gap-4 text-white">
        {status === "processing" ? (
          <>
            <div className="w-8 h-8 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" />
            <span style={{ fontFamily: "'Manrope', sans-serif" }}>Signing in with Google...</span>
          </>
        ) : (
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#EF4444" }}>Login failed. Redirecting...</span>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
